import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiUsers, FiCalendar, FiMail, FiBarChart2 } from 'react-icons/fi';
import './Dashboard.css';

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get('/api/analytics/dashboard');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (!analytics) {
    return <div className="error">Error loading dashboard data</div>;
  }

  const { summary = {}, batchBreakdown = [], attendanceByBatch = [], recentAttendance = [] } = analytics;
  
  // Ensure summary has default values
  const safeSummary = {
    totalTrainees: summary?.totalTrainees || 0,
    activeTrainees: summary?.activeTrainees || 0,
    inactiveTrainees: summary?.inactiveTrainees || 0,
    totalAttendance: summary?.totalAttendance || 0,
    attendancePercentage: summary?.attendancePercentage || 0,
    presentCount: summary?.presentCount || 0,
    totalCourses: summary?.totalCourses || 0,
    totalSMS: summary?.totalSMS || 0,
    sentSMS: summary?.sentSMS || 0,
    totalEmails: summary?.totalEmails || 0,
    sentEmails: summary?.sentEmails || 0,
  };

  return (
    <div className="dashboard">
      <div className="dashboard-banner">
        <h1>Dashboard Overview</h1>
        <p>
          Managing <strong>{safeSummary.totalTrainees}</strong> trainees across{' '}
          <strong>{batchBreakdown.length || 0}</strong> {batchBreakdown.length === 1 ? 'batch' : 'batches'}.
        </p>
      </div>

      {/* Batch Breakdown Section */}
      {batchBreakdown && batchBreakdown.length > 0 && (
        <div className="dashboard-section batch-overview-section">
          <h2>Batch Overview</h2>
          <div className="batch-stats-grid">
            {batchBreakdown.map((batch, index) => (
              <div key={batch.id} className="batch-stat-card">
                <div className="batch-card-header">
                  <h3>{batch.name}</h3>
                </div>
                <div className="batch-card-content">
                  <div className="batch-stat-item">
                    <span className="batch-stat-label">Total:</span>
                    <span className="batch-stat-value">{batch.total_trainees || 0} trainees</span>
                  </div>
                  <div className="batch-stat-item">
                    <span className="batch-stat-label">Active:</span>
                    <span className="batch-stat-value active">{batch.active_trainees || 0}</span>
                  </div>
                  <div className="batch-stat-item">
                    <span className="batch-stat-label">Inactive:</span>
                    <span className="batch-stat-value inactive">{batch.inactive_trainees || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon users">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>Total Trainees</h3>
            <p className="stat-number">{safeSummary.totalTrainees}</p>
            <p className="stat-subtitle">
              {safeSummary.activeTrainees} active
              {batchBreakdown && batchBreakdown.length > 0 && (
                <span> • {batchBreakdown.length} {batchBreakdown.length === 1 ? 'Batch' : 'Batches'}</span>
              )}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon attendance">
            <FiCalendar />
          </div>
          <div className="stat-content">
            <h3>Overall Attendance Rate</h3>
            <p className="stat-number">{safeSummary.attendancePercentage}%</p>
            <p className="stat-subtitle">{safeSummary.presentCount || 0} present / {safeSummary.totalAttendance} total</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon courses">
            <FiBarChart2 />
          </div>
          <div className="stat-content">
            <h3>Total Courses</h3>
            <p className="stat-number">{safeSummary.totalCourses}</p>
            <p className="stat-subtitle">Active courses</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon messages">
            <FiMail />
          </div>
          <div className="stat-content">
            <h3>Messages Sent</h3>
            <p className="stat-number">{safeSummary.sentSMS + safeSummary.sentEmails}</p>
            <p className="stat-subtitle">{safeSummary.sentSMS} SMS, {safeSummary.sentEmails} Emails</p>
          </div>
        </div>
      </div>

      {/* Attendance by Batch Section */}
      {attendanceByBatch && attendanceByBatch.length > 0 && (
        <div className="dashboard-section">
          <h2>Attendance by Batch</h2>
          <div className="attendance-by-batch" style={{ marginTop: '15px' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Batch Name</th>
                  <th>Total Records</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Attendance Rate</th>
                </tr>
              </thead>
              <tbody>
                {attendanceByBatch.map(batch => (
                  <tr key={batch.id}>
                    <td><strong>{batch.name}</strong></td>
                    <td>{batch.total_attendance || 0}</td>
                    <td>{batch.present_count || 0}</td>
                    <td>{batch.absent_count || 0}</td>
                    <td>
                      <span className={`rate-badge ${(batch.attendance_percentage || 0) >= 80 ? 'high' : (batch.attendance_percentage || 0) >= 60 ? 'medium' : 'low'}`}>
                        {batch.attendance_percentage || 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="dashboard-section">
        <h2>Recent Attendance</h2>
        <div className="recent-attendance">
          {recentAttendance && recentAttendance.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Date</th>
                  <th>Present</th>
                  <th>Total</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {recentAttendance.map((item, index) => {
                  const rate = item.total > 0 ? ((item.present / item.total) * 100).toFixed(1) : 0;
                  return (
                    <tr key={index}>
                      <td>{item.course_title}</td>
                      <td>{new Date(item.attendance_date).toLocaleDateString()}</td>
                      <td>{item.present}</td>
                      <td>{item.total}</td>
                      <td>
                        <span className={`rate-badge ${rate >= 80 ? 'high' : rate >= 60 ? 'medium' : 'low'}`}>
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No attendance records yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

